import React, { useContext } from "react";
import { Input, makeStyles, Typography } from "@material-ui/core";
import { Field, ErrorMessage } from "formik";
import FormError from "../../FormError";
import FMUInputField from "../../shared/FormIkMUFormComponent/FMUInputField";
import { viewportContext } from "../../../context/ViewportContext";
import { tokens } from "../../../theme/tokens";

interface NewCaseFieldProps {
  label: string;
  name: string;
  type?: string;
  disabled?: boolean;
  placeHolder?: string | null;
  isEmail?: boolean;
  required?: boolean;
  input?: boolean;
  customOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
  labelAdornment?: React.ReactNode;
}

// The J10 Field visual contract, expressed for this copy's v4 <Input>.
// Held locally (not via useCaseStyle) so this ticket's restyle does not leak to
// useCaseStyle's other consumers. The fork stays two separate implementations.
const fieldLook = {
  width: "100%",
  height: "40px",
  paddingLeft: "3px",
  backgroundColor: tokens.color.surface.default,
  border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  borderRadius: tokens.radius.sm,
  transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
  "&:hover": { borderColor: tokens.color.border.strong },
  "&.Mui-focused": { borderColor: tokens.color.focus.ring },
  "& .MuiInputBase-input": {
    textIndent: "10px",
    color: tokens.color.text.primary,
    fontSize: tokens.type.dense.size,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: tokens.color.text.disabled,
  },
} as const;

const useStyles = makeStyles(() => ({
  parentChild: {
    flex: "1",
    width: "100%",
    minWidth: 0,
  },
  parentChildTallScreen: {
    flex: "1",
    width: "100%",
    minWidth: 0,
  },
  rowContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[1],
  },
  labelContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    "& p": {
      fontSize: tokens.type.caption.size,
      fontWeight: tokens.type.caption.weight,
      lineHeight: tokens.type.caption.lineHeight,
      color: tokens.color.text.secondary,
    },
  },
  fieldContainer: {
    width: "100%",
  },
  input: { ...fieldLook },
  emailInput: { ...fieldLook },
  dateInput: {
    ...fieldLook,
    "& .MuiInputBase-input": {
      ...fieldLook["& .MuiInputBase-input"],
      textIndent: "12px",
    },
  },
  error: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.status.error,
    marginTop: tokens.space[1],
    paddingLeft: 0,
  },
}));

function NewCaseField({
  label,
  name,
  type = "text",
  disabled = false,
  placeHolder = null,
  isEmail = false,
  required = false,
  input = false,
  customOnChange = () => {},
  style = {},
  labelAdornment = null,
}: NewCaseFieldProps) {
  const { tallScreen } = useContext(viewportContext) as {
    tallScreen?: boolean;
  };
  const classes = useStyles();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    customOnChange(e);
  };

  return (
    <div
      className={
        tallScreen ? classes.parentChildTallScreen : classes.parentChild
      }
    >
      <div className={classes.rowContainer}>
        <div className={classes.labelContainer}>
          <Typography>
            {label}
            {required && (
              <span style={{ color: tokens.color.status.error, fontWeight: 500 }}>*</span>
            )}
          </Typography>
          {labelAdornment}
        </div>
        <div className={classes.fieldContainer}>
          {input ? (
            <Input
              name={name}
              type={type}
              disabled={disabled}
              fullWidth
              onChange={handleOnChange}
              disableUnderline
              className={
                isEmail
                  ? classes.emailInput
                  : type === "date"
                  ? classes.dateInput
                  : classes.input
              }
              placeholder={placeHolder ?? label}
            />
          ) : (
            <Field
              name={name}
              type={type}
              disabled={disabled}
              fullWidth
              style={style}
              disableUnderline
              className={
                isEmail
                  ? classes.emailInput
                  : type === "date"
                  ? classes.dateInput
                  : classes.input
              }
              component={FMUInputField}
              placeholder={placeHolder ?? label}
            />
          )}
        </div>
      </div>
      <div>
        <ErrorMessage
          name={name}
          component={FormError}
          className={classes.error}
        />
      </div>
    </div>
  );
}

export default NewCaseField;
